// common/calculationHelper.js
export function calculateDailyEnergyArrays(
  solarData = [],
  gridData = [], // +ve = import, -ve = export (kept for length compatibility)
  generatorData = [],
  loadData = [],
  batteryCapacityKWh = 0, // full battery capacity
  totalGridIn = 0, // kWh imported from grid
  totalGridOut = 0, // kWh exported to grid
  returntimetoindexofconstantminutes,
  batterySOCStart = null, // battery % at start of day
  batterySOCEnd = null // battery % at end of day
) {
  totalGridIn = Math.abs(totalGridIn);
  totalGridOut = Math.abs(totalGridOut);
  const intervalFactor = 1 / 12; // 5 min = 1/12 h

  function getVal(arr, i) {
    if (!arr || i >= arr.length) return 0;
    const el = arr[i];
    if (el === null || el === undefined) return 0;
    if (typeof el === "object" && "y" in el) return Number(el.y) || 0;
    return Number(el) || 0;
  }

  // totals
  let totalLoad = 0;
  let totalSolarProd = 0;
  let totalGenProd = 0;

  let solarToLoad = 0;
  let solarToBattery = 0;
  let solarToGrid = 0;
  let genToLoad = 0;

  const length = Math.min(
    solarData.length || 0,
    gridData.length || 0,
    generatorData.length || 0,
    loadData.length || 0
  );

  for (let i = 0; i < length; i++) {
    if (i <= returntimetoindexofconstantminutes) {
      const solarKW = getVal(solarData, i);
      const genKW = getVal(generatorData, i);
      const loadKW = getVal(loadData, i);

      const solarKWh = solarKW * intervalFactor;
      const genKWh = genKW * intervalFactor;
      const loadKWh = loadKW * intervalFactor;

      totalSolarProd += solarKWh;
      totalGenProd += genKWh;
      totalLoad += loadKWh;

      // Solar used for load
      const solarUsedForLoad = Math.min(solarKWh, loadKWh);
      solarToLoad += solarUsedForLoad;

      // Remaining load covered by generator
      const remainLoad = loadKWh - solarUsedForLoad;
      genToLoad += Math.min(genKWh, remainLoad);

      // Solar remaining after load goes to battery/grid
      const solarRemain = solarKWh - solarUsedForLoad;
      // split remaining between battery and grid proportionally to totalGridOut
      // If no export, all goes to battery
      if (totalGridOut > 0) {
        solarToGrid +=
          (solarRemain / (solarRemain + batteryCapacityKWh)) * totalGridOut;
        solarToBattery += solarRemain - solarToGrid;
      } else {
        solarToBattery += solarRemain;
      }
    }
  }

  // --- Battery SOC-based calculation ---
  let batteryCharged = 0;
  let batteryDischarged = 0;
  if (
    batterySOCStart !== null &&
    batterySOCEnd !== null &&
    batteryCapacityKWh > 0
  ) {
    const startKWh = (batterySOCStart / 100) * batteryCapacityKWh;
    const endKWh = (batterySOCEnd / 100) * batteryCapacityKWh;
    const diff = endKWh - startKWh;
    if (diff > 0) batteryCharged = diff;
    else if (diff < 0) batteryDischarged = Math.abs(diff);
  }

  function round2(v) {
    return Math.round(v * 100) / 100;
  }

  const totalProdSources = totalSolarProd + totalGenProd;
  const prodBase = totalProdSources > 0 ? totalProdSources : 1;

  const consBase =
    solarToLoad + totalGenProd + Number(totalGridIn || 0) + batteryDischarged >
    0
      ? solarToLoad +
        totalGenProd +
        Number(totalGridIn || 0) +
        batteryDischarged
      : 1;

  // --- Production Results ---
  const productionResults = {
    total: round2(totalProdSources),
    load: {
      value: round2(solarToLoad + genToLoad),
      percentage: round2(((solarToLoad + genToLoad) / prodBase) * 100),
    },
    battery: {
      value: round2(batteryCharged),
      percentage: round2((batteryCharged / prodBase) * 100),
    },
    grid: {
      value: -round2(Number(totalGridOut || 0)),
      percentage: round2((Number(totalGridOut || 0) / prodBase) * 100),
    },
    solarSplit: {
      toLoad: round2(solarToLoad),
      toBattery: round2(solarToBattery),
      toGrid: round2(solarToGrid),
    },
  };

  // --- Consumption Results ---
  const consumptionTotal =
    solarToLoad + totalGenProd + Number(totalGridIn || 0) + batteryDischarged;

  const consumptionResults = {
    total: round2(consumptionTotal),
    solar: {
      value: round2(solarToLoad),
      percentage: round2((solarToLoad / consBase) * 100),
    },
    battery: {
      value: round2(batteryDischarged),
      percentage: round2((batteryDischarged / consBase) * 100),
    },
    generator: {
      value: round2(totalGenProd),
      percentage: round2((totalGenProd / consBase) * 100),
    },
    grid: {
      value: round2(Number(totalGridIn || 0)),
      percentage: round2((Number(totalGridIn || 0) / consBase) * 100),
    },
  };

  const raw = {
    totalSolarProd,
    totalGenProd,
    totalProdSources,
    totalLoad,
    solarToLoad,
    solarToBattery,
    solarToGrid,
    genToLoad,
    batteryCharged,
    batteryDischarged,
    totalGridIn: Number(totalGridIn || 0),
    totalGridOut: Number(totalGridOut || 0),
    batterySOCStart,
    batterySOCEnd,
  };

  return { productionResults, consumptionResults, raw };
}
// common/calculationHelper.js
// common/calculationHelper.js
export function calculateDailySummary(
  solarData = [],
  loadData = [],
  generatorData = [],
  totalGridIn = 0, // Grid purchase (kWh)
  totalGridOut = 0, // Feed-in to grid (kWh)
  returntimetoindexofconstantminutes
) {
  const intervalFactor = 1 / 12; // 5 min intervals = 1/12 hour

  function getVal(arr, i) {
    if (!arr || i >= arr.length) return 0;
    const el = arr[i];
    if (el === null || el === undefined) return 0;
    if (typeof el === "object" && "y" in el) return Number(el.y) || 0;
    return Number(el) || 0;
  }

  let totalSolarProd = 0;
  let totalGenProd = 0;
  let totalLoad = 0;

  const length = Math.min(
    solarData.length || 0,
    loadData.length || 0,
    generatorData.length || 0
  );

  for (let i = 0; i < length; i++) {
    if (i <= returntimetoindexofconstantminutes) {
      const solarKW = getVal(solarData, i);
      const genKW = getVal(generatorData, i);
      const loadKW = getVal(loadData, i);

      totalSolarProd += solarKW * intervalFactor;
      totalGenProd += genKW * intervalFactor;
      totalLoad += loadKW * intervalFactor;
    }
  }

  function round2(v) {
    return Math.round(v * 100) / 100;
  }

  return {
    dailySolarProduction: round2(totalSolarProd),
    dailyGeneratorProduction: round2(totalGenProd),
    dailyLoadConsumption: round2(totalLoad),
    dailyFeedIn: round2(Math.abs(totalGridOut)),
    dailyGridPurchase: round2(Math.abs(totalGridIn)),
  };
}
